import React from "react";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import { Container, Row, Col } from "react-bootstrap";
import { motion } from "framer-motion";
import {
  FaBullseye,
  FaEye,
  FaBriefcase,
  FaBuilding,
  FaHome,
  FaGraduationCap,
  FaUsers,
  FaTools,
  FaCheck,
  FaArrowRight,
  FaHandshake,
  FaRegLightbulb,
  FaRegClock,
  FaAward,
  FaUserTie,
  FaCompass
} from "react-icons/fa";
import "./aboutus.css";

const Aboutus = () => {
  // Motion Animation Presets
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const scaleUp = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
  };

  // What We Do Data
  const coreServices = [
    {
      title: "Business Setup Consultation",
      icon: <FaBriefcase />,
      desc: "Our experts help you understand the complete process of establishing your company in GIFT City, including regulatory guidance, documentation, licensing, and compliance requirements.",
      features: ["IFSCA Licensing", "Entity Selection", "Compliance Advisory"]
    },
    {
      title: "Office Space Solutions",
      icon: <FaBuilding />,
      desc: "We help businesses identify and secure the ideal office space based on their team size, industry, operational requirements, and future growth plans.",
      features: ["Ready-to-move Offices", "Managed Spaces", "Corporate Leasing"],
      image: "./images/offices.webp"
    },
    {
      title: "Residential Assistance",
      icon: <FaHome />,
      desc: "Relocating employees should be easy. We assist in finding quality residential options close to GIFT City, ensuring teams settle comfortably.",
      features: ["Executive Apartments", "Rental Properties", "Family Accommodations"],
      image: "./images/house.webp"
    },
    {
      title: "School & Education Support",
      icon: <FaGraduationCap />,
      desc: "Families relocating to GIFT City often prioritize quality education. We guide them in selecting the best schools and securing admissions.",
      features: ["CBSE & ICSE Options", "IB & International Curriculums", "Admission Guidance"],
      image: "./images/schools.webp"
    },
    {
      title: "Employee Relocation Services",
      icon: <FaUsers />,
      desc: "We ensure a smooth transition for employees and their families by providing local logistics, daily essentials, utilities, and banking guidance.",
      features: ["Utility Setup", "Local Transport Info", "Banking Assistance"]
    },
    {
      title: "Business Ecosystem Support",
      icon: <FaTools />,
      desc: "Beyond office setup, we connect businesses with a trusted network of local professionals, designers, and operational vendors.",
      features: ["Legal & Tax Advisors", "Interior Designers", "IT Infra Partners"]
    }
  ];

  // Why Choose Us Data
  const whyChooseUs = [
    {
      title: "Complete End-to-End Support",
      icon: <FaAward />,
      desc: "One partner for everything—from entity setup and office selection to employee relocation and lifestyle services."
    },
    {
      title: "Local Market Expertise",
      icon: <FaCompass />,
      desc: "Deep knowledge of GIFT City's commercial, residential, and regulatory ecosystem to keep you ahead of compliance."
    },
    {
      title: "Trusted Business Network",
      icon: <FaHandshake />,
      desc: "Instant access to verified developers, property owners, consultants, schools, and professional service providers."
    },
    {
      title: "Time & Cost Efficiency",
      icon: <FaRegClock />,
      desc: "Accelerate your setup, avoid common paperwork delays, and reduce unnecessary overhead costs."
    },
    {
      title: "Personalized Consultation",
      icon: <FaRegLightbulb />,
      desc: "Every business has unique goals. We customize setup solutions specific to your industry, scale, and timeline."
    },
    {
      title: "Dedicated Relationship Manager",
      icon: <FaUserTie />,
      desc: "A single, dedicated point of contact to coordinate every aspect of your project and answer all questions."
    }
  ];

  // Strategic Advantages
  const strategicAdvantages = [
    "India's premier International Financial Services Centre (IFSC)",
    "Globally competitive business ecosystem",
    "World-class commercial infrastructure",
    "Modern Grade-A office spaces",
    "High-speed digital connectivity",
    "Sustainable smart city development",
    "Efficient urban planning",
    "Strong regulatory framework",
    "Attractive tax incentives for eligible IFSC businesses",
    "Easy access to skilled talent",
    "Growing fintech and innovation ecosystem",
    "Excellent connectivity to major Indian cities",
    "Premium residential communities",
    "International-standard educational institutions",
    "High-quality healthcare facilities",
    "Safe, clean, and well-managed environment"
  ];

  // Industries We Serve
  const industries = [
    "Financial Services", "Banking", "Insurance", "FinTech", "Information Technology",
    "Global Capability Centers (GCCs)", "Consulting Firms", "Startups", "Professional Services",
    "Investment Companies", "Family Offices", "International Businesses", "Legal & Compliance",
    "Engineering & Technology"
  ];

  // Our Process
  const processSteps = [
    { step: "01", title: "Business Consultation", desc: "Understand your business objectives, expansion plans, and operational needs." },
    { step: "02", title: "Requirement Analysis", desc: "Evaluate office space, workforce, compliance, and relocation requirements." },
    { step: "03", title: "Property Selection", desc: "Recommend suitable office spaces and premium residential options." },
    { step: "04", title: "Setup & Registration", desc: "Assist with documentation, approvals, registrations, and onboarding." },
    { step: "05", title: "Employee Relocation", desc: "Help employees and families settle into homes, schools, and local services." },
    { step: "06", title: "Ongoing Support", desc: "Continue supporting your business ecosystem as it grows in GIFT City." }
  ];

  return (
    <div className="about-page-wrapper">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section className="about-hero-section">
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={6}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="hero-content"
              >
                <span className="section-subtitle-badge">ABOUT GCC GIFTCITY</span>
                <h1 className="hero-title">
                  Empowering Businesses to Establish & Grow in <span>GIFT City</span>
                </h1>
                <p className="hero-description">
                  At <strong>GCC Giftcity</strong>, we simplify the journey of establishing your business in <strong>GIFT City</strong>, India's first operational smart city and International Financial Services Centre (IFSC).
                </p>
                <p className="hero-subtext">
                  Whether you are an entrepreneur, startup, SME, multinational corporation, fintech company, or global enterprise, we provide end-to-end guidance to help you successfully establish your office. Our mission is to make your business expansion seamless by offering complete support—from choosing the right office space to helping your employees settle comfortably with housing, schools, healthcare, and lifestyle services.
                </p>
                <div className="hero-btn-container mt-4">
                  <a href="#services" className="cta-primary-btn">
                    Explore Our Services <FaArrowRight />
                  </a>
                </div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={scaleUp}
                className="collage-wrapper"
              >
                <div className="collage-grid">
                  <div className="collage-item large-image">
                    <img src="./images/offices.webp" alt="Grade-A Office Spaces" />
                    <div className="collage-badge">Premium Offices</div>
                  </div>
                  <div className="collage-item small-image-1">
                    <img src="./images/house.webp" alt="Residential Accommodations" />
                    <div className="collage-badge">Elite Housing</div>
                  </div>
                  <div className="collage-item small-image-2">
                    <img src="./images/schools.webp" alt="International Schools" />
                    <div className="collage-badge">Top-tier Schools</div>
                  </div>
                </div>
                {/* Floating stat card */}
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="floating-stat-card"
                >
                  <h3>100%</h3>
                  <p>Hassle-Free Setup</p>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission & Vision Section */}
      <section className="mission-vision-section">
        <Container>
          <Row className="gy-4">
            <Col md={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInUp}
                className="mission-card h-100"
              >
                <div className="card-icon-box">
                  <FaBullseye />
                </div>
                <h3>Our Mission</h3>
                <p>
                  To become India's most trusted business setup partner by providing complete ecosystem support for companies planning to establish their presence in GIFT City.
                </p>
              </motion.div>
            </Col>
            <Col md={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInUp}
                className="vision-card h-100"
              >
                <div className="card-icon-box">
                  <FaEye />
                </div>
                <h3>Our Vision</h3>
                <p>
                  To create a one-stop platform where businesses from across India and around the world can easily discover, establish, and grow their operations in GIFT City while enjoying world-class infrastructure, business-friendly regulations, and exceptional quality of life.
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* What We Do Section */}
      <section id="services" className="what-we-do-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">WHAT WE DO</span>
            <h2>Comprehensive Business Setup & Relocation Services</h2>
            <p className="section-description">
              We provide comprehensive business setup and relocation services designed to simplify every step of your expansion.
            </p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="services-grid mt-5"
          >
            <Row className="g-4">
              {coreServices.map((service, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div variants={fadeInUp} className="service-item-card h-100">
                    <div className="service-card-top">
                      <div className="service-icon">{service.icon}</div>
                      <h4>{service.title}</h4>
                    </div>
                    <p className="service-desc">{service.desc}</p>
                    <div className="service-features mt-3">
                      {service.features.map((feat, fIdx) => (
                        <div key={fIdx} className="feature-pill">
                          <FaCheck className="check-icon" /> {feat}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">PARTNER FOR GROWTH</span>
            <h2>Why Choose Us</h2>
            <p className="section-description">
              We tailor our solutions to your industry, company size, and expansion goals, acting as your local strategic partner.
            </p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="why-grid mt-5"
          >
            <Row className="g-4">
              {whyChooseUs.map((item, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div variants={fadeInUp} className="why-card h-100">
                    <div className="why-icon-box">{item.icon}</div>
                    <h5>{item.title}</h5>
                    <p>{item.desc}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* GIFT City Advantages Section */}
      <section className="advantages-section">
        <div className="decor-circle-top"></div>
        <div className="decor-circle-bottom"></div>
        <Container>
          <Row className="align-items-center gy-5">
            <Col lg={5}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                className="advantages-intro"
              >
                <span className="section-subtitle-badge light">STRATEGIC ADVANTAGES</span>
                <h2>Why Businesses Choose GIFT City</h2>
                <p>
                  Establishing your office in GIFT City provides several long-term benefits, making it India's premium gateway for international financial operations and capability centers.
                </p>
                <div className="advantages-accent-card mt-4">
                  <h5>Global Standards</h5>
                  <p>A globally competitive business ecosystem with cutting edge smart infrastructure and direct tax benefits.</p>
                </div>
              </motion.div>
            </Col>
            <Col lg={7}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
                className="advantages-grid-box"
              >
                <Row className="g-3">
                  {strategicAdvantages.map((adv, index) => (
                    <Col sm={6} key={index}>
                      <motion.div variants={fadeInUp} className="adv-item-card">
                        <div className="adv-check"><FaCheck /></div>
                        <span>{adv}</span>
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Industries We Serve Section */}
      <section className="industries-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">VERSATILE EXPERIENCE</span>
            <h2>Industries We Serve</h2>
            <p className="section-description">
              We support businesses across diverse sectors seeking to capitalize on GIFT City's unique advantages.
            </p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="industries-cloud mt-5"
          >
            {industries.map((ind, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ scale: 1.05, translateY: -5, boxShadow: "0 8px 25px rgba(0, 102, 255, 0.15)" }}
                className="industry-tag"
              >
                <FaArrowRight className="tag-icon" />
                {ind}
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </section>

      {/* Our Process Section */}
      <section className="process-section">
        <Container>
          <div className="section-header text-center">
            <span className="section-subtitle-badge">OUR WORKFLOW</span>
            <h2>Our Process</h2>
            <p className="section-description">
              Our simple, step-by-step roadmap for a successful setup and seamless employee relocation.
            </p>
          </div>

          <div className="process-timeline mt-5">
            <Row className="gy-4">
              {processSteps.map((step, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={fadeInUp}
                    className="process-step-card"
                  >
                    <div className="step-number-bubble">{step.step}</div>
                    <h4>{step.title}</h4>
                    <p>{step.desc}</p>
                    {index < 5 && <div className="step-arrow-connect d-none d-lg-block"><FaArrowRight /></div>}
                  </motion.div>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </section>

      {/* Our Commitment / Bottom CTA */}
      <section className="commitment-cta-section text-center">
        <Container>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={scaleUp}
            className="commitment-content-box"
          >
            <h2>Our Commitment</h2>
            <p className="commitment-text">
              Our goal is not just to help you establish an office—it is to help you build a thriving business ecosystem in GIFT City. From your first consultation to your long-term growth, we are committed to delivering reliable guidance, trusted partnerships, and seamless execution.
            </p>
            <p className="commitment-sub">
              Whether you're launching a startup, expanding an existing enterprise, or establishing a Global Capability Centre (GCC), we are here to make your transition to GIFT City efficient, compliant, and successful.
            </p>
            <div className="cta-actions mt-4">
              <a href="mailto:access@starlighttechlabs.com" className="cta-btn-primary-white">
                Contact Our Experts
              </a>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Aboutus;
